import { getVariantItemImageUrl } from './get-product-image-url.js';

/**
 * Returns a map of variants grouped by facet term, for a given facet key.
 * If a variant has no facetTerm value for a given key, it will be grouped under `facetTerm: null`
 * ex: facetKey='color'
 * {
 *  'red': [variant1, variant2],
 *  'blue': [variant3],
 *   null: [variant4],
 * }
 */ const groupByFacetTerm = (variants, facetKey)=>{
    const facetTermMap = new Map();
    for (const variant of variants){
        const term = variant.facets[facetKey] || null;
        const variantGroup = facetTermMap.get(term) || [];
        variantGroup.push(variant);
        facetTermMap.set(term, variantGroup);
    }
    return facetTermMap;
};
/**
 * This function recursively loops through the facetConfigList and groups a list of variants by facet key and term.
 */ const buildTree = (facetConfigList, variants, facetConfigIndex)=>{
    if (facetConfigIndex >= facetConfigList.length) return [];
    const currentFacetConfig = facetConfigList[facetConfigIndex];
    // Exit the recursion when at the end of the facetConfigList
    if (!currentFacetConfig) return [];
    const facetTermMap = groupByFacetTerm(variants, currentFacetConfig.facetKey);
    return Array.from(facetTermMap, ([facetTerm, variantGroup])=>{
        const firstVariant = variantGroup[0];
        if (!firstVariant) {
            throw new Error(`Something went wrong while building the variant facet tree for facetTerm: ${facetTerm}`);
        }
        const skuCollection = variantGroup.map((v)=>v.sku
        );
        const childFacets = buildTree(facetConfigList, variantGroup, facetConfigIndex + 1);
        const hasNoChildFacetTerms = childFacets.length === 0 || childFacets.every((facet)=>facet.facetTerm === null
        );
        /**
     * If there are no child facets, but there is more than one sku in the collection,
     * Then we are on the last tree level and we need to de-group the variants in the skuCollection
     * in order to display them as separate nodes in the variant selector tree
     */ if (hasNoChildFacetTerms && skuCollection.length > 1) {
            return variantGroup.map((variant)=>{
                const degroupedFacetTerm = variant.facets[currentFacetConfig.facetKey] || null;
                return createFacetNode({
                    variant,
                    facetKey: currentFacetConfig.facetKey,
                    facetTerm: degroupedFacetTerm,
                    childFacets: [],
                    skuCollection: [
                        variant.sku
                    ]
                });
            });
        }
        const facetNode = createFacetNode({
            variant: firstVariant,
            facetKey: currentFacetConfig.facetKey,
            facetTerm,
            childFacets,
            skuCollection
        });
        return [
            facetNode
        ];
    }).flat();
};
const createFacetNode = ({ variant , facetKey , facetTerm , childFacets , skuCollection  })=>{
    const { sku , slug , displayName , media  } = variant;
    return {
        facetKey,
        facetTerm,
        childFacets,
        name: displayName,
        imageUrl: getVariantItemImageUrl(media),
        skuCollection,
        sku,
        slug
    };
};
const createVariantSelectorTree = (variants, facetConfigList)=>{
    if (!facetConfigList.length) return [];
    const publishedVariants = variants.filter((variant)=>variant.isPublished
    );
    return buildTree(facetConfigList, publishedVariants, 0);
};

export { createVariantSelectorTree };
//# sourceMappingURL=create-variant-facet-tree.js.map
